import React from 'react';
import Linkify from 'react-linkify';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Typography, TypographyProps } from '@mui/material';
import { CSSProperties } from '@mui/styles';
import Link from './Link';

const URL_TEXT_LIMIT = 50;

interface DecoratorProps {
  className?: string;
  disableAnchorIcon?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}

// @see https://github.com/tasti/react-linkify/tree/master/src/decorators
const AnchorComponentDecorator =
  (props: DecoratorProps) =>
  (
    decoratedHref: string,
    decoratedText: string,
    key: number
  ): React.ReactNode => {
    const { className, disableAnchorIcon, onClick } = props;

    return (
      <Link
        afterIcon={
          disableAnchorIcon ? undefined : (
            <OpenInNewIcon
              fontSize="small"
              style={{ verticalAlign: 'text-top' }}
            />
          )
        }
        className={className}
        component="span"
        key={key}
        href={decoratedHref}
        onClick={onClick}
        text={decoratedText}
        variant="inherit"
      />
    );
  };

const TextComponentDecorator =
  (props: DecoratorProps) =>
  (
    decoratedHref: string,
    decoratedText: string,
    key: number
  ): React.ReactNode => {
    const { className, disableAnchorIcon } = props;

    return (
      <Link
        afterIcon={
          disableAnchorIcon ? undefined : (
            <OpenInNewIcon
              fontSize="small"
              style={{ verticalAlign: 'text-top' }}
            />
          )
        }
        className={className}
        component="span"
        key={key}
        text={<Box component="span">{decoratedText}</Box>}
        variant="inherit"
      />
    );
  };

export interface TextDecoratorProps extends DecoratorProps {
  className?: string;
  component?: React.ElementType;
  /**
   * Disables the decoration as an anchor, because this component
   * could be used within other anchors, which generates invalid HTML that
   * will throw some annoying React warnings
   */
  disableAnchor?: boolean;
  disableLinkify?: boolean;
  disableTrim?: boolean;
  text: string;
  TypographyProps?: Partial<TypographyProps>;
  whiteSpace?: CSSProperties['whiteSpace'];
}

const TextDecorator = React.forwardRef<HTMLDivElement, TextDecoratorProps>(
  (props: TextDecoratorProps, ref) => {
    const {
      className,
      component = 'span',
      disableAnchor = false,
      disableAnchorIcon = false,
      disableLinkify,
      disableTrim = false,
      onClick,
      text,
      TypographyProps,
      whiteSpace,
    } = props;

    const componentDecorator = disableAnchor
      ? TextComponentDecorator({ className, disableAnchorIcon })
      : AnchorComponentDecorator({ className, disableAnchorIcon, onClick });

    const textDecorator = (urlText: string) => {
      if (!disableTrim && urlText.length > URL_TEXT_LIMIT) {
        return `${urlText.substr(0, URL_TEXT_LIMIT - 3)}...`;
      }
      return urlText;
    };

    const renderContent = () => {
      if (disableLinkify) {
        return text;
      }
      return (
        <Linkify
          componentDecorator={componentDecorator}
          textDecorator={textDecorator}
        >
          {text}
        </Linkify>
      );
    };

    return (
      <Typography
        className={className}
        component={component}
        ref={ref}
        style={{ whiteSpace, wordBreak: 'break-word' }}
        {...TypographyProps}
      >
        {renderContent()}
      </Typography>
    );
  }
);

export default TextDecorator;
